import React, { use, useCallback, useMemo, useRef } from "react";
import ScreenCard, { ScreenCardHandle } from "../ScreenCard";
import {
    CreateMissionMode,
    CreateMissionScreensContext,
} from "../../contexts/CreateMissionManager";
import { useFormatContractWorkingTime, useI18n } from "../../hooks/i18n-hooks";
import { useAtom } from "jotai";
import { newMissionState } from "../../atoms/core";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { CreateMissionStackParamList } from "../../navigations/CreateMissionStack";
import { useSnackbar } from "../../hooks/snackbar";
import { Contract, Mission, ProfileType, Document } from "../../__generated__/graphql";
import ListItem from "../../ui_atoms/ListItem";
import { useContractDisplayAttributes } from "../../hooks/missions";
import SalaryProjections from "../../components/SalaryProjections";
import { isOnDemandWorkingTime } from "../../utils";
import Text from "../../components/Text";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet, View } from "react-native";
import Gap from "../../ui_atoms/Gap";
import { useContractCct } from "../../hooks/cct-utils";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useBuildIjmDescription } from "../../hooks/ijm";
import { SHORT_DATE_FORMAT_OPTIONS, useDateFormatter } from "../../hooks/date";
import { TitleMediumText } from "../../components/FormattedText";
import VStack from "../../ui_atoms/VStack";
import ListGroup from "../../components/ListGroup";
import { useIntercom } from "../../hooks/intercom";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { Bubble } from "../../components/Bubble";
import { DocumentContext } from "../../contexts/DocumentManager";
import Switch from "../../ui_atoms/Switch";

const CreateMissionSummary = () => {
    const i18n = useI18n();
    const styles = useStyles();
    const { gridSize, colors } = useTheme();
    const navigation = useNavigation<NavigationProp<CreateMissionStackParamList>>();
    const { presentArticleId } = useIntercom();
    const { validateScreen, createMissionMode } = use(CreateMissionScreensContext);
    const [processing, setProcessing] = React.useState(false);
    const [currentMission, setCurrentMission] = useAtom(newMissionState);
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { showSnackbar } = useSnackbar();
    const formatDate = useDateFormatter();
    const formatContractWorkingTime = useFormatContractWorkingTime();
    const missionCct = useContractCct(currentMission.currentContract as Contract);
    const buildIjmDescription = useBuildIjmDescription();
    const { editDocument } = use(DocumentContext);
    const validate = React.useCallback(async () => {
        setProcessing(true);
        try {
            await validateScreen("CreateMissionSummary", navigation);
        } catch (err) {
            showSnackbar({
                mode: "error",
                text: "Error creating mission",
            });
            console.warn("error saving mission", err);
        } finally {
            setProcessing(false);
        }
    }, [navigation, showSnackbar, validateScreen]);

    React.useEffect(() => {
        const unsubscribe = navigation.addListener("focus", () => {
            setProcessing(false);
        });

        // Return the function to unsubscribe from the event so it gets removed on unmount
        return unsubscribe;
    }, [navigation, setProcessing]);
    const getDisplayAttributes = useContractDisplayAttributes();

    const displayAttributes = useMemo(
        () => getDisplayAttributes(currentMission as Mission, profileType),
        [getDisplayAttributes, currentMission, profileType],
    );

    const origin = useBubbleStepOrigin("CreateMissionSummary");

    const [hasAgreed, setHasAgreed] = React.useState(false);
    const [hasAgreedError, setHasAgreedError] = React.useState(false);

    const scrollViewRef = useRef<ScreenCardHandle>(null);

    const scrollToBottom = useCallback(() => {
        scrollViewRef.current?.scrollContentToEnd();
        setHasAgreedError(true);
    }, []);

    return (
        <ScreenCard
            ref={scrollViewRef}
            buttons={[
                {
                    title:
                        createMissionMode === CreateMissionMode.New
                            ? i18n.translate("screens.user.create_mission_summary.next_title")
                            : i18n.translate("screens.user.create_mission_summary.save_contract"),
                    onPress: !hasAgreed ? scrollToBottom : validate,
                    mode: !hasAgreed ? "disabled" : "secondary",
                    validating: processing,
                },
            ]}
            withAssistanceButton
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_summary.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                {profileType && currentMission.currentContract && (
                    <>
                        <Text style={styles.description}>
                            {i18n.t("screens.user.create_mission_summary.description")}
                        </Text>
                        <View style={{ marginHorizontal: -(gridSize / 1.5) }}>
                            <Gap gap="1/2" />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.legal_employer")}
                                description={currentMission?.legalEmployer?.marketingName || ""}
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.mission_employer")}
                                description={currentMission?.clientInfo?.name || ""}
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.talent")}
                                description={currentMission?.workerInfo?.name || ""}
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.address")}
                                description={
                                    currentMission?.currentContract?.workingAddress?.formatted || ""
                                }
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.job_title")}
                                description={[
                                    currentMission.activityType?.title,
                                    ...(currentMission.activityType?.title !==
                                    currentMission?.currentContract?.jobTitle
                                        ? [currentMission?.currentContract?.jobTitle]
                                        : []),
                                ].join("\n")}
                            />
                            {currentMission?.currentContract?.jobDescription && (
                                <ListItem
                                    title={i18n.t("screens.user.contract_details.job_description")}
                                    description={currentMission?.currentContract?.jobDescription}
                                />
                            )}
                            {!isPrivateClient && (
                                <ListItem
                                    title={i18n.t("screens.user.contract_details.cct")}
                                    description={missionCct?.name || ""}
                                />
                            )}
                            {currentMission.currentContract?.startDate && (
                                <ListItem
                                    inGroup={profileType === ProfileType.Worker}
                                    title={i18n.t("screens.user.contract_details.start_date")}
                                    description={formatDate(
                                        new Date(currentMission.currentContract.startDate),
                                        SHORT_DATE_FORMAT_OPTIONS,
                                    )}
                                />
                            )}
                            <ListItem
                                title={i18n.t("screens.user.contract_details.end_date")}
                                description={
                                    currentMission.currentContract?.endDate
                                        ? formatDate(
                                              new Date(currentMission.currentContract.endDate),
                                              SHORT_DATE_FORMAT_OPTIONS,
                                          )
                                        : i18n.t("screens.user.contract_details.no_end_date")
                                }
                            />
                            <ListItem
                                title={i18n.t(`utils.working_time.classic.title`)}
                                description={formatContractWorkingTime(
                                    currentMission.currentContract,
                                )}
                            />
                            <ListItem
                                title={i18n.t("mission.vacationDays")}
                                description={displayAttributes.vacationDays}
                            />

                            <ListItem
                                title={i18n.t("mission.payrollSource")}
                                description={i18n.t(
                                    `payrollSource.type.${currentMission.payrollSource}`,
                                    { defaultValue: currentMission.payrollSource },
                                )}
                            />
                            {currentMission.ijmSelection && (
                                <ListItem
                                    title={i18n.t("ijm.mission.title")}
                                    description={buildIjmDescription(
                                        currentMission.ijmSelection.ijmGroupName || undefined,
                                        currentMission.ijmSelection.ijmPayFirstDays,
                                        currentMission.ijmSelection.ijmPayRemainingPercents,
                                    )}
                                />
                            )}
                            <ListItem
                                title={i18n.t("mission.invoiced")}
                                description={displayAttributes.salaryInvoice}
                            />
                            <ListItem
                                title={i18n.t("mission.gross_salary")}
                                description={displayAttributes.salaryGross}
                            />
                            {displayAttributes.fees?.map((fee) => (
                                <ListItem
                                    key={fee.key}
                                    title={fee.title}
                                    description={fee.description}
                                />
                            ))}
                            {displayAttributes.deductions?.map((deduction) => (
                                <ListItem
                                    key={deduction.key}
                                    title={deduction.title}
                                    description={deduction.description}
                                />
                            ))}
                            {currentMission.documents?.length ? (
                                <VStack v="1/2" gap={1 / 2}>
                                    <VStack h="2/3">
                                        <TitleMediumText>
                                            {i18n.t("mission.annexes")}
                                        </TitleMediumText>
                                    </VStack>
                                    {currentMission.documents.map((doc) => {
                                        const document = doc as Document;
                                        return (
                                            <ListItem
                                                key={document.id}
                                                title={document.title}
                                                icon="memo"
                                                onPress={() =>
                                                    editDocument({
                                                        document,
                                                        onSave: (document) => {
                                                            setCurrentMission((prev) => {
                                                                const prevDocument =
                                                                    (prev.documents as Document[]) ||
                                                                    [];
                                                                const updatedDocuments =
                                                                    prevDocument.map((doc) =>
                                                                        doc.id === document.id
                                                                            ? document
                                                                            : doc,
                                                                    );
                                                                return {
                                                                    ...prev,
                                                                    documents: updatedDocuments,
                                                                };
                                                            });
                                                        },
                                                        onDelete: (document) => {
                                                            setCurrentMission((prev) => {
                                                                const prevDocument =
                                                                    (prev.documents as Document[]) ||
                                                                    [];
                                                                const updatedDocuments =
                                                                    prevDocument.filter(
                                                                        (doc) =>
                                                                            doc.id !== document.id,
                                                                    );
                                                                return {
                                                                    ...prev,
                                                                    documents: updatedDocuments,
                                                                };
                                                            });
                                                        },
                                                    })
                                                }
                                            />
                                        );
                                    })}
                                </VStack>
                            ) : null}
                            {/* no projection on demand, where no volume is guaranteed */}
                            {!isOnDemandWorkingTime(
                                currentMission.currentContract?.workingTimeClassicSpec,
                            ) && (
                                <>
                                    <VStack v="1/2" h="2/3">
                                        <TitleMediumText>
                                            {i18n.t("mission.salary_projections")}
                                        </TitleMediumText>
                                    </VStack>
                                    <SalaryProjections
                                        mission={currentMission as Mission}
                                        forWorker
                                    />
                                    <Gap gap="1/2" />
                                </>
                            )}
                            <VStack v="1/2" h="2/3" gap={1}>
                                <TitleMediumText>
                                    {i18n.t("mission.important_informations")}
                                </TitleMediumText>
                            </VStack>
                            <ListGroup
                                items={[
                                    {
                                        title: i18n.t(
                                            "mission.informations.essential_informations.title",
                                        ),
                                        description: i18n.t(
                                            "mission.informations.essential_informations.description",
                                            {
                                                legalEmployerName:
                                                    currentMission?.legalEmployer?.marketingName,
                                            },
                                        ),
                                        onPress: () => {
                                            void presentArticleId("11131224");
                                        },
                                        labels: [
                                            {
                                                mode: "miniCta",
                                                text: i18n.t("know_more"),
                                            },
                                        ],
                                        value: "essential_informations",
                                    },
                                    {
                                        title: i18n.t(
                                            "mission.informations.payment_deadline.title",
                                        ),
                                        description: i18n.t(
                                            "mission.informations.payment_deadline.description",
                                        ),
                                        onPress: () => {
                                            void presentArticleId("10704268");
                                        },
                                        labels: [
                                            {
                                                mode: "miniCta",
                                                text: i18n.t("know_more"),
                                            },
                                        ],
                                        value: "payment_deadline",
                                    },
                                    {
                                        title: i18n.t("mission.informations.fees.title"),
                                        description: i18n.t(
                                            "mission.informations.fees.description",
                                        ),
                                        onPress: () => {
                                            void presentArticleId("8894276");
                                        },
                                        labels: [
                                            {
                                                mode: "miniCta",
                                                text: i18n.t("know_more"),
                                            },
                                        ],
                                        value: "fees",
                                    },
                                ]}
                            />
                            <VStack v="1/2" h="2/3" gap={1}>
                                <View style={styles.switchContainer}>
                                    <Switch
                                        value={hasAgreed}
                                        trackColor={{
                                            false: hasAgreedError ? colors.error : undefined,
                                        }}
                                        onChange={() => {
                                            if (!hasAgreed) {
                                                setHasAgreedError(false);
                                            }
                                            setHasAgreed(!hasAgreed);
                                        }}
                                    />
                                    <Text
                                        style={[
                                            styles.switchLabel,
                                            hasAgreedError && { color: colors.error },
                                        ]}
                                    >
                                        {i18n.t("mission.i_agree")}
                                    </Text>
                                </View>
                            </VStack>
                        </View>
                    </>
                )}
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionSummary;

const useStyles = () => {
    const { fonts, colors, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                description: {
                    ...fonts.bodyMedium,
                },
                info: {
                    ...fonts.bodyMedium,
                    color: colors.onSurfaceDisabled,
                },
                switchContainer: {
                    flexDirection: "row",
                    alignItems: "flex-start",
                },
                switchLabel: {
                    ...fonts.bodyMedium,
                    marginLeft: gridSize / 2,
                    flex: 1,
                },
            }),
        [fonts.bodyMedium, colors.onSurfaceDisabled, gridSize],
    );
};
